import { useState, ChangeEvent, useEffect } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import { copyToClipboard } from "@/lib/utils";
import { Copy, Trash2, RotateCcw } from "lucide-react";
import { ToolPageLayout } from "@/components/ToolPageLayout";

const SAMPLE_WHITESPACE_TEXT = `   Hello,     World!   
   This   text  contains  irregular   spacing,
   trailing whitespace,  and   tabs.    `;

export function WhitespaceRemover() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState<"trim" | "collapse" | "all">("collapse");

  const processText = (text: string, currentMode: "trim" | "collapse" | "all") => {
    if (!text) {
      setOutput("");
      return;
    }
    
    let result = text;
    if (currentMode === "trim") {
      result = text.split("\n").map(line => line.trim()).join("\n");
    } else if (currentMode === "collapse") {
      result = text.replace(/[ \t]+/g, " ");
    } else if (currentMode === "all") {
      result = text.replace(/\s+/g, "");
    }
    
    setOutput(result);
  };

  useEffect(() => {
    setInput(SAMPLE_WHITESPACE_TEXT);
    processText(SAMPLE_WHITESPACE_TEXT, mode);
  }, []);

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInput(val);
    processText(val, mode);
  };

  const handleModeChange = (newMode: "trim" | "collapse" | "all") => {
    setMode(newMode);
    processText(input, newMode);
  };

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="Remove Whitespace Online"
      description="Clean your text by removing unnecessary whitespace, spaces, or tabs. Easily format your strings with our free tool."
      h1="Remove Whitespace"
      category="Text & Word"
      categoryPath="/text-word"
      howToUse={[
        "Paste your text into the input area.",
        "Choose 'Trim Lines', 'Collapse Spaces', or 'Remove All Whitespace'.",
        "Copy the cleaned text."
      ]}
      faq={[
        { question: "How to remove all whitespace from string?", answer: "Use our 'Remove All Whitespace' mode to strip every space, tab, and newline from your text." },
        { question: "How to remove extra spaces between words?", answer: "Use the 'Collapse Spaces' mode to replace multiple spaces with a single space." }
      ]}
    >
      <div className="space-y-6">
        <div className="flex gap-2">
          <Button onClick={() => handleModeChange("trim")} variant={mode === "trim" ? "default" : "outline"} size="sm">Trim Lines</Button>
          <Button onClick={() => handleModeChange("collapse")} variant={mode === "collapse" ? "default" : "outline"} size="sm">Collapse Spaces</Button>
          <Button onClick={() => handleModeChange("all")} variant={mode === "all" ? "default" : "outline"} size="sm">Remove All Whitespace</Button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Original Text</label>
            <Textarea
              placeholder="Paste text here..."
              className="min-h-[300px] whitespace-pre"
              value={input}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Processed Text</label>
            <Textarea
              className="min-h-[300px] bg-slate-50 whitespace-pre"
              value={output}
              readOnly
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={handleCopy} disabled={!output} variant="outline" className="gap-2">
            <Copy className="h-4 w-4" />
            {copied ? "Copied!" : "Copy Output"}
          </Button>
          <Button 
            onClick={() => {
              setInput(SAMPLE_WHITESPACE_TEXT);
              processText(SAMPLE_WHITESPACE_TEXT, mode);
            }} 
            variant="outline" 
            className="gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset Sample
          </Button>
          <Button onClick={() => { setInput(""); setOutput(""); }} variant="ghost" className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="h-4 w-4" />
            Clear
          </Button>
        </div>
      </div>
    </ToolPageLayout>
  );
}
